import type { C2S, S2C } from "../shared/protocol";

type Handlers = {
  onWelcome?: (m: Extract<S2C, { t: "welcome" }>) => void;
  onState?:   (m: Extract<S2C, { t: "state" }>) => void;
  onEvents?:  (m: Extract<S2C, { t: "events" }>) => void;
  onChat?:    (m: Extract<S2C, { t: "chat" }>) => void;
  onPong?:    (m: Extract<S2C, { t: "pong" }>) => void;
  onError?:   (m: Extract<S2C, { t: "error" }>) => void;
  onClose?:   () => void;
  onStatusChange?: (status: NetStatus) => void;
};

export type NetStatus =
  | "connecting"
  | "open"
  | "reconnecting"   // socket dropped, waiting before retry
  | "closed";

export class Network {
  private ws: WebSocket | null = null;
  private url: string;
  private handlers: Handlers = {};
  ping = 0;
  status: NetStatus = "closed";
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private maxRetries = 8;
  private wantOpen = false;

  constructor(arena: string) {
    if (!/^[a-z0-9-]{1,24}$/i.test(arena)) {
      throw new Error("invalid arena id");
    }
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    this.url = `${proto}//${location.host}/ws/${encodeURIComponent(arena)}`;
  }

  on(handlers: Handlers) { this.handlers = { ...this.handlers, ...handlers }; }

  private setStatus(s: NetStatus) {
    if (this.status === s) return;
    this.status = s;
    this.handlers.onStatusChange?.(s);
  }

  connect(onOpen?: () => void) {
    this.wantOpen = true;
    this.openSocket(onOpen);
  }

  private openSocket(onOpen?: () => void) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.retryAttempt = 0;
      this.setStatus("open");
      onOpen?.();
      this.pingInterval = setInterval(
        () => this.send({ t: "ping", ts: performance.now() }),
        2500,
      );
    });
    ws.addEventListener("message", (ev) => {
      // Defensive parse — never crash the loop on a malformed frame.
      let msg: S2C;
      try {
        if (typeof ev.data !== "string") return;
        if (ev.data.length > 65536) return; // ridiculous → drop
        msg = JSON.parse(ev.data) as S2C;
      } catch { return; }
      if (!msg || typeof (msg as { t?: unknown }).t !== "string") return;
      try {
        switch (msg.t) {
          case "welcome": this.handlers.onWelcome?.(msg); break;
          case "state":   this.handlers.onState?.(msg); break;
          case "events":  this.handlers.onEvents?.(msg); break;
          case "chat":    this.handlers.onChat?.(msg); break;
          case "pong": {
            this.ping = performance.now() - msg.ts;
            this.handlers.onPong?.(msg);
            break;
          }
          case "error":   this.handlers.onError?.(msg); break;
        }
      } catch (err) {
        console.error("Network handler error", err);
      }
    });
    const onCloseLike = () => {
      if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; }
      this.handlers.onClose?.();
      if (this.wantOpen) this.scheduleReconnect();
      else this.setStatus("closed");
    };
    ws.addEventListener("close", onCloseLike);
    ws.addEventListener("error", () => { try { ws.close(); } catch { /* noop */ } });
  }

  private scheduleReconnect() {
    if (!this.wantOpen) return;
    if (this.retryAttempt >= this.maxRetries) {
      this.setStatus("closed");
      return;
    }
    this.setStatus("reconnecting");
    // Exponential backoff with jitter, capped.
    const base = Math.min(15000, 500 * Math.pow(1.7, this.retryAttempt));
    const jitter = Math.random() * 250;
    const delay = base + jitter;
    this.retryAttempt++;
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  send(msg: C2S) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(msg)); } catch { /* noop */ }
    }
  }

  close() {
    this.wantOpen = false;
    if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = null;
    this.setStatus("closed");
  }
}
